import React from "react";
import MuiFormControl from "@mui/material/FormControl";
import PropTypes from "prop-types";

/**
 * FormControl component wrapper for form field grouping.
 * Wraps Material-UI FormControl with theme integration.
 * 
 * @component
 * @example
 * <FormControl fullWidth>
 *   <InputLabel>Age</InputLabel>
 *   <Select value={age} onChange={handleChange}>
 *     <MenuItem value={10}>Ten</MenuItem>
 *   </Select>
 * </FormControl>
 */
const FormControl = ({ children, ...props }) => {
  return <MuiFormControl {...props}>{children}</MuiFormControl>;
};

FormControl.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * The color of the component
   */
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
  ]),
  /**
   * If true, the component will be disabled
   */
  disabled: PropTypes.bool,
  /**
   * If true, the label will be displayed in an error state
   */
  error: PropTypes.bool,
  /**
   * If true, the component will take up the full width of its container
   */
  fullWidth: PropTypes.bool,
  /**
   * If true, the component is in focus
   */
  focused: PropTypes.bool,
  /**
   * If true, the label will indicate that the input is required
   */
  required: PropTypes.bool,
  /**
   * The size of the component
   */
  size: PropTypes.oneOf(["small", "medium"]),
  /**
   * The variant to use
   */
  variant: PropTypes.oneOf(["standard", "outlined", "filled"]),
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - maintain exact MUI FormControl behavior

export default FormControl;

